import { useState, useEffect, useRef } from 'react';
import { parseEventText } from '@/lib/nlp';
import { generateId, toDateString, parseISO, formatKoreanFull } from '@/lib/dates';
import type { CalendarEvent, EventColor } from '@/types';
import styles from './SmartInput.module.css';

const COLORS: { value: EventColor; css: string }[] = [
  { value: 'blue', css: 'var(--event-blue)' },
  { value: 'purple', css: 'var(--event-purple)' },
  { value: 'green', css: 'var(--event-green)' },
  { value: 'orange', css: 'var(--event-orange)' },
  { value: 'pink', css: 'var(--event-pink)' },
  { value: 'cyan', css: 'var(--event-cyan)' },
];

interface Props {
  selectedDate: Date;
  editingEvent?: CalendarEvent | null;
  onSave: (event: CalendarEvent) => void;
  onClose: () => void;
}

export function SmartInput({ selectedDate, editingEvent, onSave, onClose }: Props) {
  const [rawText, setRawText] = useState('');
  const [title, setTitle] = useState('');
  const [parsedDate, setParsedDate] = useState(toDateString(selectedDate));
  const [parsedStartTime, setParsedStartTime] = useState<string | null>(null);
  const [parsedEndTime, setParsedEndTime] = useState<string | null>(null);
  const [isAllDay, setIsAllDay] = useState(false);
  const [color, setColor] = useState<EventColor>('blue');
  const textRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // Edit mode: pre-fill from editingEvent
  useEffect(() => {
    if (editingEvent) {
      setTitle(editingEvent.title);
      setParsedDate(editingEvent.date);
      setParsedStartTime(editingEvent.startTime || null);
      setParsedEndTime(editingEvent.endTime || null);
      setIsAllDay(editingEvent.isAllDay);
      setColor(editingEvent.color);
      // 원본 입력 텍스트 복원
      if (editingEvent.description) {
        setRawText(editingEvent.description);
      }
    } else {
      setParsedDate(toDateString(selectedDate));
      setTimeout(() => textRef.current?.focus(), 50);
    }
  }, [editingEvent, selectedDate]);

  // Escape to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Real-time NLP parsing
  function handleRawChange(value: string) {
    setRawText(value);
    if (!value.trim()) {
      setParsedDate(toDateString(selectedDate));
      setParsedStartTime(null);
      setParsedEndTime(null);
      setIsAllDay(false);
      setTitle('');
      return;
    }
    const result = parseEventText(value, selectedDate);
    setParsedDate(result.date);
    setParsedStartTime(result.startTime);
    setParsedEndTime(result.endTime);
    setIsAllDay(result.isAllDay);
    setTitle(result.suggestedTitle);
  }

  function formatDisplayDate(dateStr: string): string {
    try {
      return formatKoreanFull(parseISO(dateStr));
    } catch {
      return dateStr;
    }
  }

  function handleSave() {
    if (!title.trim()) {
      titleRef.current?.focus();
      return;
    }
    const now = Date.now();
    const event: CalendarEvent = {
      id: editingEvent?.id || generateId(),
      title: title.trim(),
      description: rawText.trim() || undefined,
      date: parsedDate,
      startTime: isAllDay ? undefined : parsedStartTime || undefined,
      endTime: isAllDay ? undefined : parsedEndTime || undefined,
      isAllDay,
      color,
      createdAt: editingEvent?.createdAt || now,
      updatedAt: now,
    };
    onSave(event);
  }

  const isEdit = !!editingEvent;
  const hasContent = isEdit ? !!title.trim() : !!rawText.trim();

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.sheet} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.sheetHeader}>
          <span className={styles.sheetTitle}>{isEdit ? '일정 수정' : '새 일정'}</span>
          <button className={styles.closeBtn} onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4.5 4.5L13.5 13.5M13.5 4.5L4.5 13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Free-text area */}
        <div className={styles.inputArea}>
          <textarea
            ref={textRef}
            className={styles.rawTextarea}
            placeholder={"일정을 자유롭게 입력하세요\n예: 내일 오후 3시 팀 회의 2시간"}
            value={rawText}
            onChange={e => handleRawChange(e.target.value)}
            rows={3}
          />
        </div>

        {/* Parsed badges */}
        {rawText.trim() && (
          <div className={styles.badges}>
            <span className={styles.badge}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <rect x="1" y="2" width="10" height="9" rx="1.5" stroke="currentColor" strokeWidth="1" />
                <path d="M4 1v2M8 1v2M1 5h10" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
              </svg>
              {formatDisplayDate(parsedDate)}
            </span>
            {parsedStartTime && (
              <span className={styles.badge}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1" />
                  <path d="M6 3.5V6l2 1.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {parsedStartTime}{parsedEndTime ? ` – ${parsedEndTime}` : ''}
              </span>
            )}
            {isAllDay && !parsedStartTime && (
              <span className={styles.badge}>종일</span>
            )}
          </div>
        )}

        {/* Title input */}
        <div className={styles.titleSection}>
          <label className={styles.fieldLabel}>제목</label>
          <input
            ref={titleRef}
            type="text"
            className={styles.titleInput}
            placeholder="제목 입력"
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
          />
        </div>

        {/* Edit mode: date/time fields (rawText 없을 때 수동 조정용) */}
        {isEdit && !rawText.trim() && (
          <div className={styles.editFields}>
            <div className={styles.fieldRow}>
              <label className={styles.fieldLabel}>날짜</label>
              <input
                type="date"
                className={styles.fieldInput}
                value={parsedDate}
                onChange={e => setParsedDate(e.target.value)}
              />
            </div>
            <div className={styles.fieldRow}>
              <label className={styles.fieldLabel}>종일</label>
              <label className={styles.toggle}>
                <input type="checkbox" checked={isAllDay} onChange={e => setIsAllDay(e.target.checked)} />
                <span className={styles.toggleSwitch} />
              </label>
            </div>
            {!isAllDay && (
              <div className={styles.fieldRow}>
                <label className={styles.fieldLabel}>시간</label>
                <div className={styles.timeRow}>
                  <input
                    type="time"
                    className={styles.fieldInput}
                    value={parsedStartTime || ''}
                    onChange={e => setParsedStartTime(e.target.value || null)}
                  />
                  <span className={styles.timeSep}>–</span>
                  <input
                    type="time"
                    className={styles.fieldInput}
                    value={parsedEndTime || ''}
                    onChange={e => setParsedEndTime(e.target.value || null)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Color picker */}
        <div className={styles.colorRow}>
          <label className={styles.fieldLabel}>색상</label>
          <div className={styles.colorOptions}>
            {COLORS.map(c => (
              <button
                key={c.value}
                className={`${styles.colorBtn} ${color === c.value ? styles.colorActive : ''}`}
                style={{ '--swatch': c.css } as React.CSSProperties}
                onClick={() => setColor(c.value)}
                aria-label={c.value}
              />
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onClose}>취소</button>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={!hasContent || !title.trim()}
          >
            {isEdit ? '수정' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
